import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  REVIEW_ACCENT,
  getReviewPalette,
  reviewMealPlanChipSx,
  reviewMobileAmountRowSx,
  reviewMobileDetailLabelSx,
  reviewMobileDetailRowSx,
  reviewMobileDetailValueSx,
  reviewMobileRoomCardSx,
  reviewRoomNumberChipSx,
  reviewTableHeaderSx,
  reviewTableRowSx,
} from './reviewStepStyles';
import { formatReviewStayDate } from './reviewInvoiceRoomLines';

const ExtraChargesBlock = ({
  isDark,
  line,
  formatCurrency,
  compact = false,
}) => {
  const {
    extraAdultsCount,
    extraChildrenCount,
    displayExtraAdultRate,
    displayExtraChildRate,
    displayExtraAdultCharge,
    displayExtraChildCharge,
    hasExtraCharges,
    nightsToUse,
  } = line;

  if (!hasExtraCharges) return null;

  const captionSx = {
    fontSize: compact ? '0.75rem' : '0.7rem',
    display: 'block',
    color: isDark ? '#999' : '#666',
  };

  return (
    <Box sx={compact ? { mt: 0.25 } : { mt: 0.5, pt: 0.5, borderTop: `1px solid ${isDark ? '#333246' : '#e0e0e0'}` }}>
      {displayExtraAdultCharge > 0 && extraAdultsCount > 0 && displayExtraAdultRate > 0 && (
        <Typography variant="caption" sx={captionSx}>
          + {formatCurrency(displayExtraAdultRate)} × {extraAdultsCount} × {nightsToUse} = {formatCurrency(displayExtraAdultCharge)}
        </Typography>
      )}
      {displayExtraChildCharge > 0 && extraChildrenCount > 0 && displayExtraChildRate > 0 && (
        <Typography variant="caption" sx={captionSx}>
          + {formatCurrency(displayExtraChildRate)} × {extraChildrenCount} × {nightsToUse} = {formatCurrency(displayExtraChildCharge)}
        </Typography>
      )}
    </Box>
  );
};

const UnitPriceBlock = ({ line, isDark, formatCurrency, compact = false }) => {
  const { room, nightsToUse, displayRatePerNight, rowNetAmount, taxInclusive } = line;
  const ratePerNight = displayRatePerNight ?? room.ratePerNight;

  if (ratePerNight && nightsToUse) {
    const roomNetSubtotal = roundLineCurrency((ratePerNight || 0) * nightsToUse);
    return (
      <Box sx={{ textAlign: compact ? 'right' : 'right' }}>
        <Typography
          variant="body2"
          sx={{
            color: compact ? undefined : isDark ? '#999' : '#666',
            mb: compact ? 0 : 0.5,
            fontSize: compact ? '0.8125rem' : undefined,
          }}
        >
          {formatCurrency(ratePerNight)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: isDark ? '#999' : '#666', fontSize: '0.75rem', display: 'block' }}
        >
          × {nightsToUse} {nightsToUse === 1 ? 'night' : 'nights'}
        </Typography>
        {!compact && (
          <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', mt: 0.25, fontWeight: 500, display: 'block' }}>
            = {formatCurrency(taxInclusive ? rowNetAmount : roomNetSubtotal)}
          </Typography>
        )}
        <ExtraChargesBlock isDark={isDark} line={line} formatCurrency={formatCurrency} compact={compact} />
      </Box>
    );
  }

  return (
    <Typography variant="body2" sx={{ color: isDark ? '#999' : '#666', fontSize: compact ? '0.8125rem' : undefined }}>
      {formatCurrency(rowNetAmount ?? line.unitPrice)}
    </Typography>
  );
};

function roundLineCurrency(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export function ReviewInvoiceRoomMobileCard({ line, isDark, flat, total, formatCurrency, isWalkIn }) {
  const { room, index, nightsToUse, rowGstAmount, rowGstPercentage, lineAmount, roomNumber } = line;
  const reviewPalette = getReviewPalette(isDark);

  return (
    <Box sx={reviewMobileRoomCardSx(isDark, flat, index, total)}>
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
          <Typography variant="body2" sx={{ color: reviewPalette.text, fontWeight: 600 }}>
            {room.roomType}
          </Typography>
          {room.mealPlan !== 'EP' && (
            <Chip label={room.mealPlan} size="small" sx={reviewMealPlanChipSx(isDark)} />
          )}
        </Box>
        <Typography variant="caption" sx={{ color: reviewPalette.textMuted, display: 'block' }}>
          Adults: {room.adults} | Children: {room.children}
        </Typography>
        {isWalkIn && roomNumber && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            <Chip label={roomNumber} size="small" sx={reviewRoomNumberChipSx()} />
          </Box>
        )}
      </Box>

      <Box sx={reviewMobileDetailRowSx()}>
        <Typography sx={reviewMobileDetailLabelSx(isDark)}>Qty</Typography>
        <Typography sx={reviewMobileDetailValueSx(isDark)}>{room.rooms}</Typography>
      </Box>

      <Box sx={reviewMobileDetailRowSx()}>
        <Typography sx={reviewMobileDetailLabelSx(isDark)}>Stay</Typography>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          <Typography variant="caption" sx={{ color: reviewPalette.textMuted, fontSize: '0.8125rem', display: 'block' }}>
            {formatReviewStayDate(line.roomCheckIn)}
          </Typography>
          <Typography variant="caption" sx={{ color: reviewPalette.textMuted, fontSize: '0.8125rem', display: 'block' }}>
            {formatReviewStayDate(line.roomCheckOut)}
          </Typography>
          <Typography variant="caption" sx={{ color: reviewPalette.textMuted, fontSize: '0.75rem', display: 'block', mt: 0.25 }}>
            ({nightsToUse} {nightsToUse === 1 ? 'night' : 'nights'})
          </Typography>
        </Box>
      </Box>

      <Box sx={reviewMobileDetailRowSx()}>
        <Typography sx={reviewMobileDetailLabelSx(isDark)}>Unit price</Typography>
        <UnitPriceBlock line={line} isDark={isDark} formatCurrency={formatCurrency} compact />
      </Box>

      <Box sx={reviewMobileDetailRowSx()}>
        <Typography sx={reviewMobileDetailLabelSx(isDark)}>GST</Typography>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          <Typography sx={reviewMobileDetailValueSx(isDark, { accent: rowGstAmount > 0 })}>
            {formatCurrency(rowGstAmount)}
          </Typography>
          <Typography variant="caption" sx={{ color: reviewPalette.textMuted, fontSize: '0.75rem' }}>
            {rowGstPercentage > 0 ? `${rowGstPercentage}%` : '0%'}
          </Typography>
        </Box>
      </Box>

      <Box sx={reviewMobileAmountRowSx(isDark)}>
        <Typography sx={{ ...reviewMobileDetailLabelSx(isDark), fontSize: '0.8125rem' }}>Amount</Typography>
        <Typography sx={reviewMobileDetailValueSx(isDark, { accent: true, bold: true })}>
          {formatCurrency(lineAmount)}
        </Typography>
      </Box>
    </Box>
  );
}

export function ReviewInvoiceRoomDesktopRow({ line, isDark, flat, total, formatCurrency, isWalkIn }) {
  const { room, index, nightsToUse, rowGstAmount, rowGstPercentage, lineAmount, roomNumber } = line;
  const reviewPalette = getReviewPalette(isDark);

  return (
    <Box sx={reviewTableRowSx(isDark, flat, index, total)}>
      <Box sx={{ flex: '1 1 30%' }}>
        <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" component="div" sx={{ color: reviewPalette.text, fontWeight: 600 }}>
            {room.roomType}
          </Typography>
          {room.mealPlan !== 'EP' && (
            <Chip label={room.mealPlan} size="small" sx={reviewMealPlanChipSx(isDark)} />
          )}
        </Box>
        <Typography variant="caption" component="div" sx={{ color: reviewPalette.textMuted }}>
          Adults: {room.adults} | Children: {room.children}
        </Typography>
        {isWalkIn && roomNumber && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            <Chip label={roomNumber} size="small" sx={reviewRoomNumberChipSx()} />
          </Box>
        )}
      </Box>
      <Box sx={{ flex: '1 1 10%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" sx={{ color: isDark ? '#ddd' : '#333' }}>
          {room.rooms}
        </Typography>
      </Box>
      <Box sx={{ flex: '1 1 18%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.75rem' }}>
          {formatReviewStayDate(line.roomCheckIn)}
        </Typography>
        <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.75rem', mt: 0.25 }}>
          {formatReviewStayDate(line.roomCheckOut)}
        </Typography>
        <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem', mt: 0.25 }}>
          ({nightsToUse} {nightsToUse === 1 ? 'night' : 'nights'})
        </Typography>
      </Box>
      <Box sx={{ flex: '1 1 14%', textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
        <UnitPriceBlock line={line} isDark={isDark} formatCurrency={formatCurrency} />
      </Box>
      <Box sx={{ flex: '1 1 14%', textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
        <Typography variant="body2" sx={{ color: rowGstAmount > 0 ? REVIEW_ACCENT : reviewPalette.textMuted, fontWeight: 600 }}>
          {formatCurrency(rowGstAmount)}
        </Typography>
        <Typography variant="caption" sx={{ color: isDark ? '#999' : '#666', fontSize: '0.7rem' }}>
          {rowGstPercentage > 0 ? `${rowGstPercentage}%` : '0%'}
        </Typography>
      </Box>
      <Box sx={{ flex: '1 1 14%', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Typography variant="body2" sx={{ color: REVIEW_ACCENT, fontWeight: 700 }}>
          {formatCurrency(lineAmount)}
        </Typography>
      </Box>
    </Box>
  );
}

export function ReviewInvoiceRoomTableHeader({ isDark }) {
  const reviewPalette = getReviewPalette(isDark);
  return (
    <Box sx={{ ...reviewTableHeaderSx(isDark), display: { xs: 'none', md: 'flex' } }}>
      <Box sx={{ flex: '1 1 30%' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: reviewPalette.text, letterSpacing: '0.02em' }}>
          Description
        </Typography>
      </Box>
      <Box sx={{ flex: '1 1 10%', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: reviewPalette.text, letterSpacing: '0.02em' }}>
          Qty
        </Typography>
      </Box>
      <Box sx={{ flex: '1 1 18%', textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: reviewPalette.text, letterSpacing: '0.02em' }}>
          Check-in / Check-out
        </Typography>
      </Box>
      <Box sx={{ flex: '1 1 14%', textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: reviewPalette.text, letterSpacing: '0.02em' }}>
          Unit Price
        </Typography>
      </Box>
      <Box sx={{ flex: '1 1 14%', textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: reviewPalette.text, letterSpacing: '0.02em' }}>
          GST
        </Typography>
      </Box>
      <Box sx={{ flex: '1 1 14%', textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: reviewPalette.text, letterSpacing: '0.02em' }}>
          Amount
        </Typography>
      </Box>
    </Box>
  );
}
